import { IListingStatsResponse } from "../../models/Part";
import { FirstListedBucket } from "../../models/listingOptimisation/IListingInsights";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import LoadingPulse from "../shared/LoadingPulse";
import { IQueueFilters } from "./queueFilterState";

interface ITriageTilesProps {
  stats: IListingStatsResponse | undefined;
  isLoading: boolean;
  filters: IQueueFilters;
  onChange: (next: Partial<IQueueFilters>) => void;
}

interface IRow {
  key: string;
  label: string;
  value: number | undefined;
  caption: string;
  /** The swatch and the count share this colour. */
  dot: string;
  text: string;
  active: boolean;
  onClick: () => void;
}

const formatCount = (value: number | undefined): string => (value == null ? "—" : value.toLocaleString("en-GB"));

/**
 * The overview's left half: how the active listings split by the date they were FIRST listed, as
 * a stacked bar and a row per bucket, then the two signals worth working. Every row narrows the
 * queue when clicked; clicking it again clears that filter.
 */
export default function TriageTiles({ stats, isLoading, filters, onChange }: ITriageTilesProps) {
  const toggleBucket = (bucket: FirstListedBucket) =>
    onChange({ firstListedBucket: filters.firstListedBucket === bucket ? null : bucket });

  const bucketRow = (bucket: FirstListedBucket, label: string, value: number | undefined, caption: string, dot: string, text: string): IRow => ({
    key: bucket,
    label,
    value,
    caption,
    dot,
    text,
    active: filters.firstListedBucket === bucket,
    onClick: () => toggleBucket(bucket),
  });

  const over180Caption =
    stats?.firstListedOver180TotalPrice != null && stats.firstListedOver180TotalPrice > 0
      ? `${PriceFormat(stats.firstListedOver180TotalPrice)} tied up`
      : "The long tail";

  const buckets: IRow[] = [
    bucketRow("under30", "Under 30 days", stats?.firstListedUnder30, "Fresh — leave alone", "bg-green-500", "text-green-700"),
    bucketRow("d30to90", "30 – 90 days", stats?.firstListed30To90, "Check title and price", "bg-amber-400", "text-amber-700"),
    bucketRow("d90to180", "90 – 180 days", stats?.firstListed90To180, "Reprice against sold", "bg-orange-500", "text-orange-700"),
    bucketRow("over180", "Over 180 days", stats?.firstListedOver180, over180Caption, "bg-red-500", "text-red-700"),
  ];

  const signals: IRow[] = [
    {
      key: "pricedAboveIntelligence",
      label: "Priced above intelligence",
      value: stats?.pricedAboveIntelligence,
      caption: "Live price more than 10% over the intelligent price",
      dot: "bg-violet-500",
      text: "text-violet-700",
      active: filters.pricedAboveIntelligence,
      onClick: () => onChange({ pricedAboveIntelligence: !filters.pricedAboveIntelligence }),
    },
    {
      key: "missingPartNumbers",
      label: "No part numbers",
      value: stats?.missingPartNumbers,
      caption: "Neither an MPN nor an OE number",
      dot: "bg-slate-500",
      text: "text-slate-700",
      active: filters.missingPartNumbers,
      onClick: () => onChange({ missingPartNumbers: !filters.missingPartNumbers }),
    },
  ];

  const total = buckets.reduce((sum, row) => sum + (row.value ?? 0), 0);
  const loading = isLoading && !stats;

  const renderRow = (row: IRow) => (
    <li key={row.key}>
      <button
        type="button"
        onClick={row.onClick}
        aria-pressed={row.active}
        className={classNames(
          "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-primary-500/40",
          row.active ? "bg-primary-50 ring-1 ring-primary-200" : "hover:bg-gray-50"
        )}
      >
        <span className={classNames("h-2.5 w-2.5 shrink-0 rounded-full", row.dot)} aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className={classNames("block text-sm font-medium", row.active ? "text-primary-800" : "text-gray-900")}>
            {row.label}
            {row.active ? <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-primary-700">showing</span> : null}
          </span>
          <span className="block text-xs text-gray-500">{row.caption}</span>
        </span>
        {loading ? (
          <span className="w-12">
            <LoadingPulse />
          </span>
        ) : (
          <span className={classNames("shrink-0 text-xl font-semibold tabular-nums", row.text)}>{formatCount(row.value)}</span>
        )}
      </button>
    </li>
  );

  return (
    <section aria-label="Triage" className="flex min-w-0 flex-col">
      <div className="flex items-baseline justify-between gap-3 px-4 pt-3">
        <h2 className="text-sm font-semibold text-gray-900">Age of listings</h2>
        <span className="text-xs text-gray-500">{loading ? "" : `${formatCount(total)} active · from first listed`}</span>
      </div>

      <div className="px-4 pt-2.5" aria-hidden="true">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
          {total > 0 &&
            buckets.map((row) => (
              <span key={row.key} className={classNames("h-full", row.dot)} style={{ width: `${((row.value ?? 0) / total) * 100}%` }} />
            ))}
        </div>
      </div>

      <ul className="flex flex-col gap-0.5 px-1.5 pt-2">{buckets.map(renderRow)}</ul>

      <div className="mx-4 mt-2 border-t border-gray-100 pt-2">
        <span className="px-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Worth working</span>
      </div>
      <ul className="flex flex-col gap-0.5 px-1.5 pb-2 pt-1">{signals.map(renderRow)}</ul>
    </section>
  );
}
